import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Hash, Copy, RefreshCcw, Ticket, ArrowRight, Trash2, Crown, Sparkles, CalendarClock } from "lucide-react";
import Layout from "../components/Layout";
import Countdown from "../components/Countdown";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

const STORAGE_KEY = "otra_payments";

export function rememberPayment({ session_id, challenge_id, challenge_title, code }) {
  if (!session_id && !code) return;
  try {
    const list = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    // Find existing entry by session_id OR by code
    const idx = list.findIndex(
      (x) => (session_id && x.session_id === session_id) || (code && x.code === code),
    );
    const entry = {
      session_id: session_id || (idx >= 0 ? list[idx].session_id : undefined),
      challenge_id: challenge_id || (idx >= 0 ? list[idx].challenge_id : undefined),
      challenge_title: challenge_title || (idx >= 0 ? list[idx].challenge_title : "Défi"),
      code: code || (idx >= 0 ? list[idx].code : undefined),
      created_at: (idx >= 0 ? list[idx].created_at : null) || new Date().toISOString(),
    };
    if (idx >= 0) list[idx] = entry;
    else list.unshift(entry);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, 50)));
  } catch (e) {
    // ignore
  }
}

export default function MyCodes() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [statuses, setStatuses] = useState({});
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const initialFilter = (() => {
    const f = searchParams.get("filter");
    return ["all", "standard", "golden", "used", "claims"].includes(f) ? f : "all";
  })();
  const [filter, setFilter] = useState(initialFilter); // all | standard | golden | used | claims

  const refresh = useCallback(async () => {
    setLoading(true);
    // 2026 ownership rule: when connected, the BACKEND is the only source of
    // truth. localStorage is no longer surfaced for authenticated users — it
    // could leak another player's code on a shared browser.
    let merged = [];
    if (user && user !== false) {
      try {
        const { data } = await api.get("/play/me/codes");
        merged = (data?.codes || []).map((c) => ({
          code: c.code,
          code_type: c.code_type || "standard",
          origin: c.origin || "purchase",
          is_bonus: !!c.is_bonus,
          pack_type: c.pack_type,
          status: c.status || "available",
          challenge_id: c.challenge_id,
          challenge_title: c.challenge_title || "—",
          challenge_category: c.challenge_category,
          challenge_status: c.challenge_status,
          challenge_starts_at: c.challenge_starts_at,
          challenge_ends_at: c.challenge_ends_at,
          created_at: c.created_at,
          play_session_id: c.play_session_id,
          // 2026 claim metadata
          claim_eligible: !!c.claim_eligible,
          is_best: !!c.is_best,
          rank: c.rank,
          winners_count: c.winners_count,
          prize_amount: c.prize_amount,
          prize_percentage: c.prize_percentage,
          claim_status: c.claim_status,
          session_id: undefined,
        }));
      } catch (e) {
        toast.error(formatApiError(e));
        merged = [];
      }
    }
    setRows(merged);

    const next = {};
    for (const row of merged) {
      try {
        // Prefer the Stripe-independent by-code endpoint when we have the code
        if (row.code) {
          const { data } = await api.get(`/play/by-code/${row.code}`);
          next[row.session_id || row.code] = {
            payment_status: "paid",
            code: data.code,
            challenge_id: data.challenge?.id,
            session: data.session,
            challenge: data.challenge,
            nickname: data.nickname,
          };
        } else if (row.session_id) {
          const { data } = await api.get(`/payments/checkout/status/${row.session_id}`);
          next[row.session_id] = data;
          // Save code in localStorage for future calls
          if (data.code) {
            rememberPayment({
              session_id: row.session_id,
              challenge_id: data.challenge_id,
              challenge_title: row.challenge_title,
              code: data.code,
            });
          }
        }
      } catch (e) {
        next[row.session_id || row.code] = {
          error: formatApiError(e) || "Impossible de récupérer cette entrée pour le moment.",
        };
      }
    }
    setStatuses(next);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const copy = async (txt) => {
    try {
      await navigator.clipboard.writeText(txt);
      toast.success("Code copié");
    } catch {
      toast.error("Impossible de copier");
    }
  };

  const removeAll = () => {
    if (!window.confirm("Effacer l'historique local de tes paiements ? (les codes resteront actifs côté serveur)")) return;
    localStorage.removeItem(STORAGE_KEY);
    setRows([]);
    setStatuses({});
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div>
            <span className="text-xs uppercase tracking-[0.3em] text-cyan-300 font-mono-stat">
              Mon espace
            </span>
            <h1 className="font-display font-black text-4xl uppercase tracking-tight mt-2">
              Mes codes
            </h1>
            <p className="text-sm text-white/55 mt-2 max-w-lg">
              Tu retrouves ici tous les codes générés sur ce navigateur après paiement Stripe.
              Conserve-les précieusement : un code n&apos;est utilisable qu&apos;une seule fois.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 justify-start sm:justify-end">
            <button
              data-testid="mycodes-filter-all"
              onClick={() => setFilter("all")}
              className={`px-3 py-2 rounded-full text-xs font-display uppercase tracking-widest ${
                filter === "all"
                  ? "bg-white text-black"
                  : "bg-white/5 text-white/70 hover:bg-white/10"
              }`}
            >
              Tous
            </button>
            <button
              data-testid="mycodes-filter-standard"
              onClick={() => setFilter("standard")}
              className={`px-3 py-2 rounded-full text-xs font-display uppercase tracking-widest ${
                filter === "standard"
                  ? "bg-cyan-400 text-black"
                  : "bg-white/5 text-cyan-200 hover:bg-cyan-400/10"
              }`}
            >
              Standard
            </button>
            <button
              data-testid="mycodes-filter-golden"
              onClick={() => setFilter("golden")}
              className={`flex items-center gap-1 px-3 py-2 rounded-full text-xs font-display uppercase tracking-widest ${
                filter === "golden"
                  ? "bg-amber-400 text-black"
                  : "bg-white/5 text-amber-200 hover:bg-amber-400/10"
              }`}
            >
              <Crown className="w-3 h-3" />
              Golden
            </button>
            <button
              data-testid="mycodes-filter-used"
              onClick={() => setFilter("used")}
              className={`px-3 py-2 rounded-full text-xs font-display uppercase tracking-widest ${
                filter === "used"
                  ? "bg-white/90 text-black"
                  : "bg-white/5 text-white/70 hover:bg-white/10"
              }`}
            >
              Utilisés
            </button>
            <button
              data-testid="mycodes-filter-claims"
              onClick={() => setFilter("claims")}
              className={`flex items-center gap-1 px-3 py-2 rounded-full text-xs font-display uppercase tracking-widest ${
                filter === "claims"
                  ? "bg-emerald-400 text-black"
                  : "bg-white/5 text-emerald-200 hover:bg-emerald-400/10"
              }`}
            >
              Réclamer mes gains
            </button>
            <button
              data-testid="mycodes-refresh-button"
              onClick={refresh}
              disabled={loading}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-sm font-display uppercase tracking-wider disabled:opacity-50"
            >
              <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline">Actualiser</span>
            </button>
            {rows.length > 0 && (
              <button
                onClick={removeAll}
                className="p-2.5 rounded-lg bg-white/5 hover:bg-pink-500/20 hover:text-pink-300 transition-all"
                title="Effacer l'historique local"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {!user || user === false ? (
          <div
            data-testid="mycodes-login-required"
            className="panel rounded-2xl p-12 text-center"
          >
            <Ticket className="w-10 h-10 mx-auto text-cyan-300 mb-3" />
            <h3 className="font-display text-xl font-bold mb-2">
              Connecte-toi pour retrouver tes codes
            </h3>
            <p className="text-white/55 text-sm mb-6 max-w-md mx-auto">
              Tes codes, tes scores et tes gains sont liés à ton compte.
              Connecte-toi pour les afficher en toute sécurité.
            </p>
            <button
              data-testid="mycodes-login-button"
              onClick={() => navigate("/login?next=/mes-codes")}
              className="px-5 py-3 rounded-xl bg-cyan-400 text-black font-display font-bold uppercase tracking-widest hover:bg-cyan-300 active:scale-95 transition-all"
            >
              Se connecter
            </button>
          </div>
        ) : rows.length === 0 ? (
          <div data-testid="mycodes-empty" className="panel rounded-2xl p-12 text-center">
            <Ticket className="w-10 h-10 mx-auto text-cyan-300 mb-3" />
            <h3 className="font-display text-xl font-bold mb-2">Aucun code pour l&apos;instant</h3>
            <p className="text-white/55 text-sm mb-6">
              Achète un défi pour générer ton premier code personnel.
            </p>
            <button
              onClick={() => navigate("/")}
              className="px-5 py-3 rounded-xl bg-cyan-400 text-black font-display font-bold uppercase tracking-widest hover:bg-cyan-300 active:scale-95 transition-all"
            >
              Voir les défis
            </button>
          </div>
        ) : (
          <ul className="space-y-3">
            {rows
              .filter((r) => {
                if (filter === "all") return true;
                if (filter === "used") {
                  return ["reserved", "consumed", "completed", "no_show", "expired"].includes(
                    r.status,
                  );
                }
                if (filter === "claims") {
                  return r.claim_status != null;
                }
                // standard / golden tabs now ONLY surface AVAILABLE codes.
                // Used ones live in the "Utilisés" tab so the available-list
                // stays readable when the player owns many codes.
                return (
                  (r.code_type || "standard") === filter && r.status === "available"
                );
              })
              .map((r) => {
              const key = r.session_id || r.code;
              const s = statuses[key] || {};
              const code = s.code || r.code;
              const paid = s.payment_status === "paid" || !!s.code_status || !!r.status;
              // Fallback to r.status (from /api/play/me/codes) when no Stripe
              // status cache exists (e.g. admin-minted codes).
              const codeStatus = s.code_status || r.status;
              const sessionSnap = s.session;
              const isWinner = sessionSnap && sessionSnap.is_winner;
              const challengeStatus =
                (sessionSnap && sessionSnap.challenge?.status) || r.challenge_status;
              const challengeFinalized = challengeStatus === "finalized";
              const challengePending = challengeStatus === "pending_finalization";
              const sessionExpired = sessionSnap?.status === "expired";
              const sessionCompleted = sessionSnap?.status === "completed";
              const isAvailable = codeStatus === "available";
              const isReserved = codeStatus === "reserved";
              const isNoShow =
                codeStatus === "no_show" || codeStatus === "expired";
              const isCancelled =
                codeStatus === "cancelled" || codeStatus === "refunded";
              return (
                <li
                  key={key}
                  data-testid={`mycodes-row-${key}`}
                  className={`rounded-2xl p-5 ${
                    r.code_type === "golden"
                      ? "border border-amber-400/50 bg-gradient-to-br from-amber-400/10 via-amber-400/5 to-transparent shadow-[0_0_30px_rgba(251,191,36,0.15)]"
                      : "panel"
                  }`}
                >
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="text-[10px] uppercase tracking-[0.3em] font-mono-stat text-cyan-300">
                          Défi
                        </div>
                        {r.code_type === "golden" && (
                          <span
                            data-testid={`mycodes-golden-badge-${r.code}`}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-400/15 border border-amber-400/50 text-amber-200 text-[9px] uppercase tracking-widest font-display font-bold"
                          >
                            <Crown className="w-2.5 h-2.5" />
                            Golden
                          </span>
                        )}
                        {r.is_bonus && (
                          <span
                            data-testid={`mycodes-bonus-badge-${r.code}`}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-fuchsia-400/15 border border-fuchsia-400/50 text-fuchsia-200 text-[9px] uppercase tracking-widest font-display font-bold"
                          >
                            <Sparkles className="w-2.5 h-2.5" />
                            Bonus
                          </span>
                        )}
                        {r.origin === "admin" && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-white/10 text-white/60 text-[9px] uppercase tracking-widest font-mono-stat">
                            Offert
                          </span>
                        )}
                      </div>
                      <div className="font-display font-bold text-lg truncate">
                        {(s.challenge && s.challenge.title) ||
                          r.challenge_title ||
                          (codeStatus === "available"
                            ? "— (à choisir)"
                            : "—")}
                      </div>
                      <div className="text-xs text-white/40 font-mono-stat mt-1">
                        {new Date(r.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </div>
                      {isAvailable && (
                        <div
                          data-testid={`mycodes-available-${code}`}
                          className="mt-2 text-xs text-emerald-300 font-mono-stat uppercase tracking-widest"
                        >
                          ✨ Code disponible — choisis un défi
                        </div>
                      )}
                      {isReserved && (
                        <div
                          data-testid={`mycodes-reserved-${code}`}
                          className="mt-2 text-xs text-fuchsia-300 font-mono-stat uppercase tracking-widest"
                        >
                          🔒 Inscrit — réservé à ce défi
                        </div>
                      )}
                      {isReserved && r.challenge_starts_at && (
                        <div
                          data-testid={`mycodes-reserved-countdown-${code}`}
                          className="mt-1.5 flex items-center gap-1.5 text-[11px] font-mono-stat"
                        >
                          <CalendarClock className="w-3 h-3" />
                          {r.challenge_status === "open" ? (
                            <Countdown
                              target={r.challenge_ends_at}
                              kind="closing"
                              dataTestid={`mycodes-reserved-countdown-value-${code}`}
                              className="text-emerald-300"
                            />
                          ) : (
                            <Countdown
                              target={r.challenge_starts_at}
                              kind="opening"
                              dataTestid={`mycodes-reserved-countdown-value-${code}`}
                              className="text-cyan-200"
                            />
                          )}
                        </div>
                      )}
                      {isNoShow && (
                        <div
                          data-testid={`mycodes-noshow-${code}`}
                          className="mt-2 text-xs text-pink-300 font-mono-stat uppercase tracking-widest"
                        >
                          ⌛ No-show — fenêtre du défi expirée
                        </div>
                      )}
                      {isCancelled && (
                        <div
                          data-testid={`mycodes-cancelled-${code}`}
                          className="mt-2 text-xs text-white/40 font-mono-stat uppercase tracking-widest"
                        >
                          ✖ Défi annulé par l&apos;administrateur
                        </div>
                      )}
                      {isWinner && challengeFinalized && (
                        r.is_best && r.claim_eligible ? (
                          <div
                            data-testid={`mycodes-best-retained-${code}`}
                            className="mt-2 text-xs text-amber-300 font-mono-stat uppercase tracking-widest"
                          >
                            🏆 Tu es gagnant · #{r.rank} · {(r.prize_amount || 0).toFixed(2)} €
                            {r.prize_percentage > 0 ? ` (${Number(r.prize_percentage).toFixed(0)} %)` : ""} · meilleur score retenu
                          </div>
                        ) : r.is_best && r.claim_status === "pending" ? (
                          <div
                            data-testid={`mycodes-claim-pending-${code}`}
                            className="mt-2 text-xs text-emerald-300 font-mono-stat uppercase tracking-widest"
                          >
                            ⏳ Réclamation envoyée — versement en attente
                          </div>
                        ) : r.is_best && r.claim_status === "paid" ? (
                          <div className="mt-2 text-xs text-emerald-300 font-mono-stat uppercase tracking-widest">
                            ✓ Gain versé
                          </div>
                        ) : r.claim_status === "not_retained" ? (
                          <div
                            data-testid={`mycodes-not-retained-${code}`}
                            className="mt-2 text-xs text-white/45 font-mono-stat uppercase tracking-widest"
                          >
                            Tentative non retenue — ton meilleur score sur ce défi est associé à un autre code
                          </div>
                        ) : (
                          <div className="mt-2 text-xs text-amber-300 font-mono-stat uppercase tracking-widest">
                            🏆 Top {r.winners_count || "—"} · meilleur score retenu sur un autre code
                          </div>
                        )
                      )}
                      {challengePending && sessionCompleted && (
                        <div
                          data-testid={`mycodes-pending-${code}`}
                          className="mt-2 text-xs text-orange-300 font-mono-stat uppercase tracking-widest"
                        >
                          ⏳ Classement en attente de finalisation
                        </div>
                      )}
                      {sessionExpired && (
                        <div
                          data-testid={`mycodes-expired-${code}`}
                          className="mt-2 text-xs text-pink-300 font-mono-stat uppercase tracking-widest"
                        >
                          ⌛ Session expirée
                        </div>
                      )}
                    </div>
                    {paid && code ? (
                      <div className="flex items-center gap-2">
                        <div
                          className={`px-4 py-2 rounded-xl border ${
                            r.code_type === "golden"
                              ? "bg-black/60 border-amber-400/60 shadow-[0_0_18px_rgba(251,191,36,0.35)]"
                              : "bg-black/60 border-cyan-400/40 glow-primary"
                          }`}
                        >
                          <div
                            className={`text-[9px] uppercase tracking-[0.3em] font-mono-stat ${
                              r.code_type === "golden" ? "text-amber-300" : "text-cyan-300"
                            }`}
                          >
                            {r.code_type === "golden" ? "Golden Code" : "Code"}
                          </div>
                          <div className="font-mono-stat font-bold text-lg tracking-[0.25em] text-white">
                            {code}
                          </div>
                        </div>
                        <button
                          onClick={() => copy(code)}
                          className="p-2.5 rounded-lg bg-white/5 hover:bg-cyan-400/20 hover:text-cyan-300 transition-all"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`mycodes-play-${code}`}
                          onClick={() => {
                            if (isNoShow || isCancelled) return;
                            // Best-score winner with claim_eligible → go to the
                            // session result screen which carries the claim UI.
                            // Other (non-retained) finalists must NOT see a
                            // "Réclamer" button — they go to the leaderboard.
                            if (isWinner && challengeFinalized && !r.claim_eligible) {
                              navigate(`/leaderboard/${r.challenge_id}`);
                              return;
                            }
                            if (sessionSnap?.session_id)
                              navigate(`/play/session/${sessionSnap.session_id}`);
                            else navigate(`/play?code=${code}`);
                          }}
                          disabled={isNoShow || isCancelled}
                          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg ${
                            isNoShow || isCancelled
                              ? "bg-white/10 text-white/30 cursor-not-allowed"
                              : isWinner && challengeFinalized && r.claim_eligible
                              ? "bg-amber-400 hover:bg-amber-300 text-black"
                              : isWinner && challengeFinalized && !r.claim_eligible
                              ? "bg-white/10 hover:bg-white/15 text-white/70"
                              : isAvailable
                              ? "bg-emerald-400 hover:bg-emerald-300 text-black"
                              : "bg-cyan-400 hover:bg-cyan-300 text-black"
                          } transition-all text-sm font-display font-bold uppercase`}
                        >
                          <Hash className="w-3.5 h-3.5" />
                          {isWinner && challengeFinalized && r.claim_eligible
                            ? "Réclamer"
                            : isWinner && challengeFinalized
                            ? "Classement"
                            : isAvailable
                            ? "Choisir un défi"
                            : isReserved && challengeStatus !== "open"
                            ? "En attente"
                            : "Jouer"}
                          <ArrowRight className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ) : s.error ? (
                      <span className="text-xs text-pink-300 max-w-xs text-right">
                        Cette entrée n&apos;a pas pu être récupérée. Le code reste valide — utilise « J&apos;ai un code » dans l&apos;entête.
                      </span>
                    ) : (
                      <span className="text-xs text-white/40 font-mono-stat uppercase tracking-widest">
                        {s.payment_status === "pending" ? "En attente..." : "Vérification..."}
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Layout>
  );
}
